import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { CM2Dataset } from '@interfaces/central-monitoring';
import {
    ConvertedControlsHubPanelObservations,
    statusRAGChips,
} from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType, NamedItemType } from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';
import {
    convertYesNoToBoolean,
    stringDateWithoutTimeFromCurrentUtcDate,
    stringDateWithoutTimeFromUtc,
} from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { cm2Panel_OpraQueries } from '@queries/generated/central-monitoring.generated';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

type Props = {
    studyRef: string;
    panelData?: cm2Panel_OpraQueries;
    setSelected: (obs: ConvertedControlsHubPanelObservations) => void;
    dataset: CM2Dataset;
};

export const CmControlsObservationsGrid = ({ studyRef, panelData, setSelected, dataset }: Props) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.popOut.obsActions.observationsGrid' });
    const { data } = ControlsHubApi.useGetControlsHubChartObservations(studyRef, panelData?.cm2Panel?.id);
    const { data: datasets } = DatasetApi.useGetActive(studyRef);
    const [gridData, setGridData] = React.useState<unknown[]>([]);
    const { ChipCell, ActiveInactiveCell, TooltipCell } = createGridCustomCells();
    const { t: tRiskLog, ready } = useTranslation('ram', { keyPrefix: 'riskLog' });
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const chips = useMemo(() => statusRAGChips(tRiskLog), []);
    const { data: controlsReviewLogDecisionData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROLS_REVIEW_LOG_DECISION },
    });
    const [datasetList, setDatasetList] = React.useState<string[] | undefined>(undefined);
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    // eslint-disable-next-line max-len
    const exportFileName = `${studyName}_Dataset ${stringDateWithoutTimeFromUtc(dataset.dataCutDate, 'DD-MMM-YYYY')}_${t('exportFileName')}_${panelData?.cm2Panel?.plotTitle}`;

    useEffect(() => {
        if (datasets) {
            const sortedDatasets = datasets
                .filter((d) => d.dataCutDate != null) // exclude undefined/null
                .sort((a, b) => new Date(b.dataCutDate!).getTime() - new Date(a.dataCutDate!).getTime())
                .map((d) => stringDateWithoutTimeFromUtc(d.dataCutDate!, 'DD-MMM-yyyy')!);

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setDatasetList(sortedDatasets);
        }
    }, [datasets]);

    useEffect(() => {
        if (data && ready && controlsReviewLogDecisionData && datasetList) {
            const convertedResult: ConvertedControlsHubPanelObservations[] = data.map((item) => {
                return {
                    ...item,
                    owner: {
                        name: item.owner_name,
                        active: convertYesNoToBoolean(item.owner_name_active),
                    },
                    association: {
                        name: item.association,
                        active: convertYesNoToBoolean(item.association_active),
                    },
                    panel: {
                        id: item.panel_id,
                        name: item.panel,
                        panelRef: item.panel_ref,
                        active: convertYesNoToBoolean(item.panel_active),
                    },
                    decision: {
                        name: item.decision,
                        active: convertYesNoToBoolean(item.decision_active),
                    },
                    time_period: stringDateWithoutTimeFromUtc(item.time_period),
                    observation_date: stringDateWithoutTimeFromUtc(item.observation_date),
                    dataset_opened_on: stringDateWithoutTimeFromUtc(item.dataset_opened_on, 'DD-MMM-yyyy'),
                    recent_action: stringDateWithoutTimeFromUtc(item.recent_action),
                    updated_date: stringDateWithoutTimeFromUtc(item.updated_date),
                    created_date: stringDateWithoutTimeFromUtc(item.created_date),
                    closed_date: stringDateWithoutTimeFromUtc(item.closed_date),
                    closed_date_populated_on: stringDateWithoutTimeFromUtc(item.closed_date_populated_on),
                };
            });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setGridData(convertedResult);
        }
    }, [data, ready, controlsReviewLogDecisionData, datasetList]);

    if (!datasetList) return;

    const columns: GridColumnProps[] = [
        {
            id: 'status',
            field: 'status',
            title: t('columns.status'),
            lockedByDefault: true,
            filter: 'text',
            minWidth: 150,
            lovData: [tRiskLog('shared.open'), tRiskLog('shared.closed')],
        },
        {
            id: 'timePeriod',
            field: 'time_period',
            title: t('columns.timePeriod'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'observationDate',
            field: 'observation_date',
            title: t('columns.observationDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'observation',
            field: 'observation',
            title: t('columns.observation'),
            lockedByDefault: true,
            filter: 'text',
            minWidth: 250,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'owner',
            field: 'owner',
            title: t('columns.owner'),
            filterKey: 'name',
            filter: 'text',
            minWidth: 150,
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'timePeriodLatestStatus',
            field: 'time_period_latest_status',
            title: t('columns.timePeriodLatestStatus'),
            filter: 'text',
            minWidth: 180,
            lovData: chips.filter((c) => c.value !== tRiskLog('shared.none')).map((c) => c.value),
            cells: { data: ChipCell(chips, { width: 95 }) },
        },
        {
            id: 'decision',
            field: 'decision',
            title: t('columns.decision'),
            filter: 'text',
            filterKey: 'name',
            minWidth: 150,
            lovData: controlsReviewLogDecisionData?.namedItemsByType
                ? controlsReviewLogDecisionData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'datasetOpenedOn',
            field: 'dataset_opened_on',
            title: t('columns.datasetOpenedOn'),
            minWidth: 150,
            lovData: datasetList,
        },
        {
            id: 'numReviews',
            field: 'reviews',
            title: t('columns.numReviews'),
            filter: 'numeric',
            width: 120,
        },
        {
            id: 'numOpenActions',
            field: 'open_actions',
            title: t('columns.numOpenActions'),
            filter: 'numeric',
            width: 120,
        },
        {
            id: 'recentAction',
            field: 'recent_action',
            title: t('columns.recentAction'),
            minWidth: 200,
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
        },
        {
            id: 'root_cause',
            field: 'root_cause',
            title: t('columns.rootCause'),
            filter: 'text',
            minWidth: 200,
        },
        {
            id: 'comments',
            field: 'comments',
            title: t('columns.comments'),
            filter: 'text',
            minWidth: 200,
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            id: 'updatedDate',
            field: 'updated_date',
            title: t('columns.updatedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'closedDate',
            field: 'closed_date',
            title: t('columns.closedDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
        },
        {
            id: 'closedDatePopulatedOn',
            field: 'closed_date_populated_on',
            title: t('columns.closedDatePopulatedOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 180,
            showByDefault: false,
        },
        {
            id: 'updatedBy',
            field: 'updated_by',
            title: t('columns.updatedBy'),
            filter: 'text',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'createdDate',
            field: 'created_date',
            title: t('columns.createdDate'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            cellName: 'DateCell',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'createdBy',
            field: 'created_by',
            title: t('columns.createdBy'),
            filter: 'text',
            minWidth: 150,
            showByDefault: false,
        },
        {
            id: 'id',
            field: 'record_id',
            title: t('columns.id'),
            filter: 'text',
            minWidth: 100,
            showByDefault: false,
        },
    ];

    return (
        <Grid
            excludeHiddenRowMenuActions
            title={t('title')}
            maxHeight="100%"
            testDataId="ram-cm-controls-observations-grid"
            data={gridData}
            columns={columns}
            filterable
            selectable={{ enabled: true, mode: 'single' }}
            onSelectionChange={(e: GridSelectionChangeEvent) => {
                const record = e.startDataItem as ConvertedControlsHubPanelObservations;
                if (!record) {
                    return;
                }
                setSelected(record);
            }}
            showAddIcon={false}
            useLoadingOverlay
            scrollable="virtual"
            dataItemKey="id"
            exportFilename={`${exportFileName}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`}
            service={AuditServiceType.CORE}
            area={AuditApplicationArea.RISK_ASSESSMENT}
            dataType={AuditDataType.EXCEL_EXPORT}
            removeCardBorder
            persistentStorageKey={String(PersistentStorageKeys['cm-controls-popout-controls-observations'])}
        />
    );
};
